import { useEffect, useState, useCallback } from "react";
import { PaymentService, PaymentMethodVM, PaymentMethodEntityTypesEnum } from "@propertelligent/client-api";
import apiHelper from "../../../utils/apiHelper";
import PlaidScreen from "../../../newComponents/PlaidScreen/PlaidScreen";
import AddPlaidAccount from "../../../components/firstTimeUserExperience/AddPlaidAccount";
import { QuickstartProvider } from "../../../components/firstTimeUserExperience/Context";
import PaymentAccountCardItem from "../../../newDashboard/Pages/payment-accounts/PaymentAccountCardItem";
import AddButtonWithArrow from "../../../components/firstTimeUserExperience/commonCom/AddButtonWithArrow";
import TextButton from "../../../newComponents/buttons/text-button";
import H2 from "../../../newComponents/typography/H2";
import SimpleSpinner from "../../../components/loader";
import RightSideModal from "../../../newComponents/Modal/right-side-modal";
import Card from "../../../newComponents/card";
import EditPlaidAccountNameModal from "../../../newDashboard/Pages/payment-accounts/EditPlaidAccountNameModal";
import "./TenantPaymentMethodTab.css";
import { UserLoginData } from "../../../components/common/charts/Enums";

interface TenantPaymentMethodTabProps {
  tenantId?: number;
}

// Max number of payment methods a tenant can add (client-side guard; backend enforces too).
const TENANT_PAYMENT_METHOD_LIMIT = 3;

const TenantPaymentMethodTab = ({ tenantId }: TenantPaymentMethodTabProps) => {
  const [rows, setRows] = useState<PaymentMethodVM[]>([]);
  const [loading, setLoading] = useState(true);
  const [rowCount, setRowCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isReconnectModalOpen, setReconnectModalOpen] = useState(false);
  const [reconnectPaymentMethod, setReconnectPaymentMethod] = useState<PaymentMethodVM | null>(null);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [isRenameModalOpen, setRenameModalOpen] = useState(false);
  const [renamePaymentMethod, setRenamePaymentMethod] = useState<PaymentMethodVM | null>(null);
  const resolvedTenantId = Number(tenantId ?? sessionStorage.getItem(UserLoginData.tenant_id));
  const fetchData = useCallback(
    async (page: number = 1) => {
      if (!resolvedTenantId) return;
      try {
        setLoading(true);
        const response = await apiHelper(
          () =>
            PaymentService.getApiPaymentMethodTenantlist(
              resolvedTenantId,
              10,
              page,
              undefined,
              undefined,
              undefined,
              undefined
            ),
          { showNotification: false }
        );
        const data = response?.data;
        if (data?.success) {
          setRows(data.success || []);
          setRowCount(data.recordCount || 0);
          setCurrentPage(page);
          setHasMore((data.recordCount || 0) > page * 10);
        }
      } catch (error) {
        console.error("Error fetching tenant payment methods:", error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [resolvedTenantId]
  );

  useEffect(() => {
    if (resolvedTenantId) {
      fetchData(1);
    }
  }, [resolvedTenantId, fetchData]);

  const handlePlaidSuccess = () => {
    setReconnectModalOpen(false);
    setReconnectPaymentMethod(null);
    fetchData(1);
  };

  const handleReconnectPlaid = (paymentMethod: PaymentMethodVM) => {
    setReconnectPaymentMethod(paymentMethod);
    setReconnectModalOpen(true);
  };

  const handleRenamePlaidAccount = (paymentMethod: PaymentMethodVM) => {
    setRenamePaymentMethod(paymentMethod);
    setRenameModalOpen(true);
  };

  const handleAddAccountSuccess = () => {
    setAddModalOpen(false);
    fetchData(1);
  };

  const hasLinkedAccount = rows.length > 0;
  const isAtPaymentMethodLimit = rowCount >= TENANT_PAYMENT_METHOD_LIMIT;

  if (!resolvedTenantId) return null;

  return (
    <div className="tenant-payment-method-tab">
      {loading && rows.length === 0 ? (
        <div className="tenant-payment-method-loading">
          <SimpleSpinner myClassName="Icon50" />
        </div>
      ) : hasLinkedAccount ? (
        <>
          <div className="tenant-payment-method-header">
            <H2 text={`Payment Accounts (${rowCount})`} />
            <span
              title={
                isAtPaymentMethodLimit
                  ? `You can add up to ${TENANT_PAYMENT_METHOD_LIMIT} payment accounts.`
                  : undefined
              }
            >
              <AddButtonWithArrow
                buttonname="Add Account"
                disabled={isAtPaymentMethodLimit}
                onClick={() => {
                  if (isAtPaymentMethodLimit) return;
                  setAddModalOpen(true);
                }}
              />
            </span>
          </div>
          <PaymentAccountCardItem
            dataHookState={{
              rows,
              loading,
              hasMore,
              fetchData,
              currentPage,
            }}
            showEditAction={false}
            onReconnectPlaid={handleReconnectPlaid}
            onRenamePlaidAccount={handleRenamePlaidAccount}
            hasAnyPaymentAccounts={true}
            className="tenant-payment-account-card"
            alwaysAllowActions={true}
          />
          <RightSideModal
            isOpen={isReconnectModalOpen}
            onClose={() => {
              setReconnectModalOpen(false);
              setReconnectPaymentMethod(null);
            }}
            title="Reconnect Plaid Account"
            maxWidth={500}
            buttonlabel={undefined}
            formId={undefined}
            customFooter={
              <div className="d-flex justify-content-end gap-2">
                <TextButton
                  label="Cancel"
                  onClick={() => {
                    setReconnectModalOpen(false);
                    setReconnectPaymentMethod(null);
                  }}
                />
                {reconnectPaymentMethod && (
                  <QuickstartProvider
                    plaidReconnectPaymentMethod={reconnectPaymentMethod}
                    plaidButtonLabel="Reconnect"
                  >
                    <AddPlaidAccount
                      onPlaidSuccess={handlePlaidSuccess}
                      showFinishButton={false}
                    />
                  </QuickstartProvider>
                )}
              </div>
            }
          >
            <Card className="no-bg">
              <PlaidScreen />
            </Card>
          </RightSideModal>
          <RightSideModal
            isOpen={isAddModalOpen}
            onClose={() => setAddModalOpen(false)}
            title="Add Payment Account"
            maxWidth={500}
            buttonlabel={undefined}
            formId={undefined}
            customFooter={
              <div className="d-flex justify-content-end gap-2">
                <TextButton label="Cancel" onClick={() => setAddModalOpen(false)} />
                {isAddModalOpen && (
                  <QuickstartProvider
                    plaidEntityConfig={{
                      entityTypeId: PaymentMethodEntityTypesEnum.Tenant,
                      entityId: resolvedTenantId,
                    }}
                    plaidButtonLabel="Continue"
                  >
                    <AddPlaidAccount
                      onPlaidSuccess={handleAddAccountSuccess}
                      showFinishButton={false}
                    />
                  </QuickstartProvider>
                )}
              </div>
            }
          >
            <Card className="no-bg">
              <PlaidScreen />
            </Card>
          </RightSideModal>

          <EditPlaidAccountNameModal
            isOpen={isRenameModalOpen}
            paymentMethod={renamePaymentMethod}
            onClose={() => {
              setRenameModalOpen(false);
              setRenamePaymentMethod(null);
            }}
            onRenamed={() => {
              setRenameModalOpen(false);
              setRenamePaymentMethod(null);
              fetchData(1);
            }}
          />
        </>
      ) : (
        <div className="tenant-payment-method-plaid-view">
          <PlaidScreen />
          <div className="tenant-payment-method-actions">
            <QuickstartProvider
              plaidEntityConfig={{
                entityTypeId: PaymentMethodEntityTypesEnum.Tenant,
                entityId: resolvedTenantId,
              }}
              plaidButtonLabel="Continue"
            >
              <AddPlaidAccount
                onPlaidSuccess={handlePlaidSuccess}
                showFinishButton={false}
              />
            </QuickstartProvider>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantPaymentMethodTab;
